type UnityWindow = Window & typeof globalThis & {
    __unityFrameBatchedResizeObserverInstalled?: boolean;
};

/**
 * Deliver resize observations at most once per animation frame.
 *
 * Report canvases contain several independently responsive surfaces (charts,
 * data grids, drawers, and overflow labels). Opening a drawer can resize all
 * of them at once. Native observers are allowed to report another resize in
 * the same delivery cycle, which browsers surface as an undelivered-observer
 * error even when the final layout is valid. Moving delivery to the next frame
 * gives every surface one stable set of bounds and removes that feedback loop.
 */
export const installFrameBatchedResizeObserver = (): void => {
    if (typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') {
        return;
    }

    const unityWindow = window as UnityWindow;
    if (unityWindow.__unityFrameBatchedResizeObserverInstalled) {
        return;
    }

    const NativeResizeObserver = window.ResizeObserver;

    class FrameBatchedResizeObserver extends NativeResizeObserver {
        private frameId: number | null = null;
        private pendingEntries: ResizeObserverEntry[] = [];
        private isDisconnected = false;

        constructor(callback: ResizeObserverCallback) {
            let instance: FrameBatchedResizeObserver;
            super((entries) => {
                instance.pendingEntries = entries;
                if (instance.frameId !== null) {
                    return;
                }
                instance.frameId = window.requestAnimationFrame(() => {
                    instance.frameId = null;
                    if (instance.isDisconnected) {
                        return;
                    }
                    const pendingEntries = instance.pendingEntries;
                    instance.pendingEntries = [];
                    callback(pendingEntries, instance);
                });
            });
            instance = this;
        }

        observe(target: Element, options?: ResizeObserverOptions): void {
            this.isDisconnected = false;
            super.observe(target, options);
        }

        disconnect(): void {
            this.isDisconnected = true;
            this.pendingEntries = [];
            if (this.frameId !== null) {
                window.cancelAnimationFrame(this.frameId);
                this.frameId = null;
            }
            super.disconnect();
        }
    }

    Object.defineProperty(window, 'ResizeObserver', {
        configurable: true,
        writable: true,
        value: FrameBatchedResizeObserver,
    });
    unityWindow.__unityFrameBatchedResizeObserverInstalled = true;
};

